/** Linhas do dia (docs/design: hora, cliente e serviço, situação e WhatsApp; livres com "Marcar"). */
import { type FreeGap, formatHour, formatHourOf, freeTimeLabel, nextSessionLabel } from '@lindeza/core';
import type { AgendaItem } from '@lindeza/core/hooks';
import { useRef } from 'react';
import { messageFor, openWhatsapp, useMessageCtx } from '../../app/useMessages';
import { IconCheck, IconCheckCheck, IconHouse, IconLink, IconLock, IconPackage, IconPlus, IconTriangleAlert, IconWhatsapp } from '../../components/Icons';

type Status = { text: string; tone?: 'warn' | 'ok' | 'danger'; icon?: typeof IconCheck };

/** A situação que importa em cada linha (uma só, para a linha não virar uma lista de selos). */
function statusOf(item: AgendaItem): Status | null {
  const a = item.appointment;
  if (item.conflict) return { text: 'choque', tone: 'danger', icon: IconTriangleAlert };
  if (a.status === 'pending') return { text: 'pedido, falta aceitar', tone: 'warn', icon: IconTriangleAlert };
  if (a.status === 'completed') return { text: 'concluído', icon: IconCheckCheck };
  if (a.status === 'canceled') return { text: 'cancelado', tone: 'danger' };
  if (a.status === 'no_show') return { text: 'faltou', tone: 'danger' };
  if (a.status === 'confirmed') return { text: 'confirmada', tone: 'ok', icon: IconCheck };
  if (a.source === 'link') return { text: 'pelo link', icon: IconLink };
  return { text: 'não confirmou', tone: 'warn', icon: IconTriangleAlert };
}

function StatusText({ s, className }: { s: Status; className?: string }) {
  const Icon = s.icon;
  return (
    <span className={`status${s.tone ? ` status--${s.tone}` : ''}${className ? ` ${className}` : ''}`}>
      {Icon && <Icon />}
      {s.text}
    </span>
  );
}

export function ApptRow({ item, timezone, onOpen }: { item: AgendaItem; timezone: string; onOpen: () => void }) {
  const ctx = useMessageCtx();
  const { appointment: a, client, service } = item;
  const inactive = a.status === 'canceled' || a.status === 'no_show';
  const status = statusOf(item);
  // No celular o WhatsApp só aparece quando há o que pedir (confirmação); no tablet, em todas.
  const needsWa = a.status === 'scheduled' && a.source !== 'link';
  const cls = [
    'ag-row',
    a.status === 'completed' || inactive ? 'is-done appt--done' : '',
    a.status === 'pending' ? 'is-pending' : '',
    item.conflict ? 'is-conflict' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const sideStatus = status && (a.status === 'scheduled' && a.source === 'link' ? { ...status, text: 'Marcou pelo link' } : status);
  return (
    <article
      className={cls}
      style={{ ['--c' as string]: service?.color ?? undefined }}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      aria-label={`${formatHourOf(a.start_utc, timezone)} ${client?.name ?? ''}, ${service?.name ?? ''}${status ? `, ${status.text}` : ''}`}
      data-testid="appointment-card"
    >
      <span className="ag-time">{formatHourOf(a.start_utc, timezone)}</span>
      <div className="ag-who">
        <b className="ag-name">{client?.name ?? 'Cliente'}</b>
        <span className="ag-meta">
          <span className="svc">{service?.name ?? 'Serviço'}</span>
          {item.packageBalance && (
            <span className="svc">
              <IconPackage width={16} height={16} /> {nextSessionLabel(item.packageBalance)}
            </span>
          )}
          {a.location === 'home' && (
            <span className="svc">
              <IconHouse width={16} height={16} /> domicílio
            </span>
          )}
          {status && <StatusText s={status} className="st-inline" />}
        </span>
      </div>
      {sideStatus && <StatusText s={sideStatus} className="st-side" />}
      {ctx && !inactive && a.status !== 'pending' ? (
        <button
          className={`wa-btn${needsWa ? '' : ' wa-btn--extra'}`}
          aria-label={`WhatsApp para ${client?.name ?? 'cliente'}`}
          onClick={(e) => {
            e.stopPropagation();
            openWhatsapp(client?.whatsapp, messageFor(ctx, 'auto', a, client, service));
          }}
        >
          <IconWhatsapp />
        </button>
      ) : (
        <span />
      )}
    </article>
  );
}

/** Horário livre: toque marca; toque longo bloqueia. Livre menor que 30 min só informa. */
export function FreeRow({ gap, onTap, onLongPress }: { gap: FreeGap; onTap: () => void; onLongPress: () => void }) {
  const timer = useRef<number | null>(null);
  const longPressed = useRef(false);
  const clear = () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
  };
  const time = formatHour(gap.time);
  if (gap.minutes < 30) {
    return (
      <div className="ag-row is-gap">
        <span className="ag-time">{time}</span>
        <span className="ag-free">{freeTimeLabel(gap.minutes)}</span>
      </div>
    );
  }
  return (
    <button
      className="ag-row is-free"
      data-testid={`free-${gap.time}`}
      onPointerDown={() => {
        longPressed.current = false;
        clear();
        timer.current = window.setTimeout(() => {
          longPressed.current = true;
          onLongPress();
        }, 550);
      }}
      onPointerUp={clear}
      onPointerLeave={clear}
      onPointerCancel={clear}
      onContextMenu={(e) => {
        e.preventDefault();
        if (!longPressed.current) {
          clear();
          longPressed.current = true;
          onLongPress();
        }
      }}
      onClick={() => {
        if (longPressed.current) return;
        onTap();
      }}
      aria-label={`${time}, ${freeTimeLabel(gap.minutes)}. Toque para marcar, segure para bloquear.`}
    >
      <span className="ag-time">{time}</span>
      <span className="ag-free">{freeTimeLabel(gap.minutes)}</span>
      <span className="btn btn--soft" aria-hidden="true">
        <IconPlus strokeWidth={2.5} /> Marcar
      </span>
    </button>
  );
}

export function BlockRow({ start, end, reason, timezone, onOpen }: { start: string; end: string; reason: string | null; timezone: string; onOpen: () => void }) {
  return (
    <button className="ag-row is-block" onClick={onOpen}>
      <span className="ag-time">{formatHourOf(start, timezone)}</span>
      <span className="ag-free">
        <IconLock /> {reason ?? 'Bloqueado'}, até {formatHourOf(end, timezone)}
      </span>
      <span />
    </button>
  );
}
